//pages/dev/DevBroadcast.js
import { useState, useEffect, useMemo } from "react";
import { db } from "../../firebase/config";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import "../../styles/DevBroadcast.css";

const PLAN_OPTIONS = ["Basic", "Standard", "Premium", "Training", "Helpdesk"];
const SEVERITIES = [
  { value: "info", label: "Info", icon: "💡" },
  { value: "low", label: "Low", icon: "⚠️" },
  { value: "high", label: "High", icon: "🚨" },
];

export default function DevBroadcast() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [url, setUrl] = useState("");
  const [severity, setSeverity] = useState("info");
  const [targetPlans, setTargetPlans] = useState([]);
  const [targetCompanyIdsCsv, setTargetCompanyIdsCsv] = useState("");
  const [sending, setSending] = useState(false);

  // Recent (dev-only; silently ignores rules errors)
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    const qy = query(
      collection(db, "platformAnnouncements"),
      orderBy("createdAt", "desc"),
      limit(20)
    );
    const off = onSnapshot(
      qy,
      (snap) => setRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return () => off();
  }, []);

  const ids = useMemo(
    () =>
      targetCompanyIdsCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [targetCompanyIdsCsv]
  );

  const togglePlan = (p) =>
    setTargetPlans((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );

  const reset = () => {
    setTitle("");
    setMessage("");
    setUrl("");
    setSeverity("info");
    setTargetPlans([]);
    setTargetCompanyIdsCsv("");
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      toast.error("Title and message are required.");
      return;
    }

    const payload = {
      title: title.trim(),
      message: message.trim(),
      url: url.trim() || null,
      severity,
      ...(targetPlans.length ? { targetPlans } : {}),
      ...(ids.length ? { targetCompanyIds: ids } : {}),
      createdAt: serverTimestamp(),
    };

    try {
      setSending(true);
      await addDoc(collection(db, "platformAnnouncements"), payload);
      toast.success("Announcement queued. Fan-out will start shortly.");
      reset();
    } catch (e) {
      console.error(e);
      toast.error("Failed to create announcement.");
    } finally {
      setSending(false);
    }
  };

  // Live preview data
  const preview = useMemo(
    () => ({
      title: title || "Announcement title",
      message:
        message ||
        "Use this space to describe the update, maintenance window, or new feature.",
      url: url || "",
      severity,
    }),
    [title, message, url, severity]
  );

  return (
    <div className="dbx">
      <header className="dbx-hero">
        <button
          className="dbx-back"
          type="button"
          onClick={() => navigate("/admin-dashboard")}
        >
          ← Back to Dashboard
        </button>
        <div className="dbx-hero-title">
          <span className="dbx-pill">Dev</span>
          <h1>Broadcast announcements</h1>
        </div>
        <p>
          Create a document in <code>/platformAnnouncements</code>. The cloud
          function will fan-out a <code>platformUpdates</code> notification to
          each targeted company.
        </p>
      </header>

      <div className="dbx-grid">
        {/* Form */}
        <section className="dbx-card dbx-form">
          <form onSubmit={handleSend}>
            <div className="dbx-section">
              <h2 className="dbx-section-title">Content</h2>

              <div className="dbx-field">
                <label htmlFor="title">
                  Title <span className="req">*</span>
                </label>
                <input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Scheduled maintenance"
                  required
                />
              </div>

              <div className="dbx-field">
                <label htmlFor="message">
                  Message <span className="req">*</span>
                </label>
                <textarea
                  id="message"
                  rows={7}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Clear description of the update or outage."
                  required
                />
                <div className="dbx-hint">
                  Keep it concise. Use the URL field for details or a status
                  page.
                </div>
              </div>

              <div className="dbx-row">
                <div className="dbx-field">
                  <label>Severity</label>
                  <div className="dbx-segment">
                    {SEVERITIES.map((s) => (
                      <button
                        type="button"
                        key={s.value}
                        className={`dbx-seg ${
                          severity === s.value ? "active" : ""
                        } seg-${s.value}`}
                        onClick={() => setSeverity(s.value)}
                        aria-pressed={severity === s.value}
                      >
                        <span className="seg-ic">{s.icon}</span>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="dbx-field">
                  <label htmlFor="url">Optional URL</label>
                  <input
                    id="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://status.yourapp.com/post/123"
                    inputMode="url"
                  />
                </div>
              </div>
            </div>

            <div className="dbx-section">
              <h2 className="dbx-section-title">Targeting</h2>

              <div className="dbx-field">
                <label>Plans (optional)</label>
                <div className="dbx-chips">
                  {PLAN_OPTIONS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`dbx-chip ${
                        targetPlans.includes(p) ? "on" : ""
                      }`}
                      onClick={() => togglePlan(p)}
                      aria-pressed={targetPlans.includes(p)}
                    >
                      {targetPlans.includes(p) && (
                        <span className="check">✓</span>
                      )}
                      {p}
                    </button>
                  ))}
                </div>
                <div className="dbx-hint">
                  Leave empty to send to all plans.
                </div>
              </div>

              <div className="dbx-field">
                <label htmlFor="ids">Company IDs (optional)</label>
                <input
                  id="ids"
                  value={targetCompanyIdsCsv}
                  onChange={(e) => setTargetCompanyIdsCsv(e.target.value)}
                  placeholder="4GHYrCHl1jSkC3ZKNJwTbAE9SYP2, abcd123..."
                />
                {!!ids.length && (
                  <div className="dbx-hint">
                    Targeting <strong>{ids.length}</strong>{" "}
                    {ids.length === 1 ? "company" : "companies"}.
                  </div>
                )}
              </div>
            </div>

            <div className="dbx-actions">
              <button
                type="button"
                className="btn ghost"
                onClick={reset}
                disabled={sending}
              >
                Reset
              </button>
              <button type="submit" className="btn primary" disabled={sending}>
                {sending ? "Sending…" : "Send announcement"}
              </button>
            </div>
          </form>
        </section>

        {/* Side column: live preview + recent */}
        <aside className="dbx-side">
          <div className="dbx-card dbx-preview">
            <div className="preview-head">
              <span className={`badge sev-${preview.severity}`}>
                {preview.severity}
              </span>
              <div className="preview-title">{preview.title}</div>
            </div>
            <div className="preview-body">{preview.message}</div>
            {preview.url ? (
              <a
                className="preview-link"
                href={preview.url}
                target="_blank"
                rel="noreferrer"
              >
                Learn more ↗
              </a>
            ) : null}
          </div>

          <div className="dbx-card">
            <h2 className="dbx-card-title">Recent</h2>
            {!recent.length ? (
              <div className="dbx-empty">No announcements yet.</div>
            ) : (
              <ul className="dbx-timeline">
                {recent.map((r) => (
                  <li key={r.id} className="dbx-tl-item">
                    <div className="dbx-tl-dot" />
                    <div className="dbx-tl-content">
                      <div className="dbx-tl-row">
                        <span className={`badge sev-${r.severity || "info"}`}>
                          {r.severity || "info"}
                        </span>
                        <strong className="dbx-tl-title">{r.title}</strong>
                      </div>
                      <div className="dbx-tl-sub">
                        {(r.targetPlans && r.targetPlans.join(", ")) ||
                          "All plans"}
                        {r.url ? (
                          <>
                            {" · "}
                            <a href={r.url} target="_blank" rel="noreferrer">
                              link
                            </a>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
